import type { SellerLimitsDTO } from '@sellsen/generated-sources';
import { DownOutlined } from '@ant-design/icons';
import { Popover, Progress, Typography } from 'antd';
import dayjs from 'dayjs';

const { Text } = Typography;

const formatCount = (value: number | undefined): string =>
  (value ?? 0).toLocaleString('tr-TR');

export const formatSellerLimit = (
  used: number | undefined,
  limit: number | undefined
): string => `${formatCount(used)} / ${formatCount(limit)}`;

const percentOf = (used: number | undefined, limit: number | undefined): number => {
  if (!limit || limit <= 0) {
    return 0;
  }
  return Math.min(100, Math.round(((used ?? 0) / limit) * 100));
};

const strokeOf = (percent: number): string => {
  if (percent >= 100) {
    return '#ff4d4f';
  }
  if (percent >= 80) {
    return '#faad14';
  }
  return '#1890ff';
};

const PROGRESS_WIDTH = 120;
const MUTED = '#8c8c8c';

type MeterProps = {
  label: string;
  used?: number;
  limit?: number;
  extra?: string;
};

/** Popover içindeki bir kota satırı: etiket · kullanım/limit · çubuk (+ isteğe bağlı dipnot). */
const Meter = ({ label, used, limit, extra }: MeterProps) => {
  const percent = percentOf(used, limit);
  return (
    <>
      <Text style={{ fontSize: 12, color: MUTED, whiteSpace: 'nowrap' }}>{label}</Text>
      <Text
        style={{
          fontSize: 12,
          fontWeight: 600,
          whiteSpace: 'nowrap',
          fontVariantNumeric: 'tabular-nums',
          textAlign: 'right',
        }}
      >
        {formatSellerLimit(used, limit)}
      </Text>
      <Progress
        percent={percent}
        showInfo={false}
        size={[PROGRESS_WIDTH, 4]}
        strokeColor={strokeOf(percent)}
        style={{ margin: 0, width: PROGRESS_WIDTH, lineHeight: 1 }}
      />
      {extra ? (
        <Text
          style={{
            fontSize: 11,
            color: MUTED,
            whiteSpace: 'nowrap',
            gridColumn: '2 / 4',
            textAlign: 'right',
          }}
        >
          {extra}
        </Text>
      ) : null}
    </>
  );
};

type SellerLimitUsageProps = {
  limits?: SellerLimitsDTO | null;
};

/**
 * Üst çubuktaki kota göstergesi. Header'da yalnız tek bir halka durur (en dolu kota — kullanıcı
 * ilk bakışta en kritik olanı görmeli); üç satırlık döküm hover/tıklama ile popover'da açılır.
 * Eski 3 satırlık grid header'ı ~220px işgal edip menüyü taşırıyordu.
 */
export const SellerLimitUsage = ({ limits }: SellerLimitUsageProps) => {
  if (!limits) {
    return null;
  }

  const resetLabel = limits.dailySearchResetsAt
    ? `${dayjs(limits.dailySearchResetsAt).format('HH:mm')}'te sıfırlanır`
    : undefined;

  const meters: MeterProps[] = [
    {
      label: 'Ürün+Havuz',
      used: limits.catalogProductUsed,
      limit: limits.catalogProductLimit,
    },
    {
      label: 'Arama',
      used: limits.dailySearchAsinUsed,
      limit: limits.dailySearchAsinLimit,
      extra: resetLabel,
    },
    {
      label: 'ASIN Havuzu',
      used: limits.asinPoolUsed,
      limit: limits.asinPoolLimit,
    },
  ];

  const maxPercent = Math.max(
    ...meters.map((meter) => percentOf(meter.used, meter.limit))
  );

  const content = (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `max-content max-content ${PROGRESS_WIDTH}px`,
        columnGap: 12,
        rowGap: 6,
        alignItems: 'center',
      }}
    >
      {meters.map((meter) => (
        <Meter key={meter.label} {...meter} />
      ))}
    </div>
  );

  return (
    <Popover
      title="Kota kullanımı"
      content={content}
      trigger={['hover', 'click']}
      placement="bottomRight"
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          height: 32,
          padding: '0 8px',
          borderRadius: 6,
          background: 'rgba(0,0,0,0.04)',
          cursor: 'pointer',
        }}
      >
        <Progress
          type="circle"
          percent={maxPercent}
          size={28}
          strokeWidth={10}
          showInfo={false}
          strokeColor={strokeOf(maxPercent)}
        />
        <Text
          style={{ fontSize: 12, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}
        >
          %{maxPercent}
        </Text>
        <DownOutlined style={{ fontSize: 10, color: MUTED }} />
      </div>
    </Popover>
  );
};
